// Backend base URL. Defaults to the MIS API on :4005; override with
// REACT_APP_API_URL (e.g. in client/.env) for other environments.
// The backend enables CORS, so direct cross-origin calls work in dev and prod.
const BASE = `${'https://api.formidium.blueastercapital.com'}/api`;

async function request(path, options = {}) {
  const res = await fetch(`${BASE}${path}`, {
    headers: { 'Content-Type': 'application/json' },
    ...options,
  });
  const data = await res.json().catch(() => ({}));
  if (!res.ok) throw new Error(data.error || `Request failed (${res.status})`);
  return data;
}

export const api = {
  // Authenticate against the MIS backend. Returns { token, user } or throws.
  login: (username, password) =>
    request('/login', { method: 'POST', body: JSON.stringify({ username, password }) }),
  funds: () => request('/funds'),
  // Latest available report date for a fund (YYYY-MM-DD or null).
  latestDate: (fund) => request(`/latest-date?fund=${encodeURIComponent(fund)}`),
  dashboard: (fund, date) =>
    request(`/dashboard?fund=${encodeURIComponent(fund)}&date=${encodeURIComponent(date)}`),
  portfolio: (fund, date) =>
    request(`/portfolio?fund=${encodeURIComponent(fund)}&date=${encodeURIComponent(date)}`),
  investor: (fund, date) =>
    request(`/investor?fund=${encodeURIComponent(fund)}&date=${encodeURIComponent(date)}`),
  performanceDashboard: (fund, date) =>
    request(`/performance-dashboard?fund=${encodeURIComponent(fund)}&date=${encodeURIComponent(date)}`),
  gainLoss: (fund, date) =>
    request(`/gain-loss?fund=${encodeURIComponent(fund)}&date=${encodeURIComponent(date)}`),
  gainLossExport: async (fund, date, tab) => {
    const qs = new URLSearchParams({ fund, date, tab }).toString();
    const res = await fetch(`${BASE}/gain-loss/export?${qs}`);
    if (!res.ok) {
      const data = await res.json().catch(() => ({}));
      throw new Error(data.error || `Export failed (${res.status})`);
    }
    const blob = await res.blob();
    const cd = res.headers.get('Content-Disposition') || '';
    const m = cd.match(/filename="?([^"]+)"?/);
    return { blob, filename: m ? m[1] : 'gain-loss.xlsx' };
  },
  balanceSheet: (fund, endDate) =>
    request('/balance-sheet', { method: 'POST', body: JSON.stringify({ fund, endDate }) }),
  // Generic passthrough — body is the exact Formidium request body.
  raw: (endpoint, body) =>
    request(`/raw/${endpoint}`, { method: 'POST', body: JSON.stringify(body || {}) }),
  // Download the NAV Pack workbook. Returns { blob, filename }; throws on error.
  navPack: async (params) => {
    const res = await fetch(`${BASE}/nav-pack?${new URLSearchParams(params).toString()}`);
    if (!res.ok) {
      const data = await res.json().catch(() => ({}));
      throw new Error(data.error || `Export failed (${res.status})`);
    }
    const blob = await res.blob();
    const cd = res.headers.get('Content-Disposition') || '';
    const m = cd.match(/filename="?([^"]+)"?/);
    return { blob, filename: m ? m[1] : 'nav-pack.xlsx' };
  },
};
